import { useCallback, useMemo, useReducer } from 'react';
import { initialState, reducer } from './types/state';
import { normalizeDomain, validateDomain } from './lib/validate';
import { useDomainCheck } from './hooks/useDomainCheck';
import { useShortlist } from './hooks/useShortlist';
import { MAX_DOMAINS_PER_REQUEST } from '@domaintriage/shared';
import { Layout } from './components/Layout';
import { InputScreen } from './components/InputScreen';
import { CheckProgress } from './components/CheckProgress';
import { ResultsTable } from './components/ResultsTable';
import { DrillInPanel } from './components/DrillInPanel';
import { ShortlistView } from './components/ShortlistView';
import { PrivacyPage } from './components/PrivacyPage';

/**
 * Parse and validate the raw input text into a list of domains ready to submit.
 */
function buildDomainList(
  inputText: string,
  mode: 'A' | 'B',
  tlds: string[],
): string[] {
  if (!inputText.trim()) return [];

  const lines = inputText.split(/\n/);
  const all: string[] = [];

  for (const line of lines) {
    const parts = line.split(/,/);
    for (const part of parts) {
      const normalized = normalizeDomain(part);
      if (!normalized) continue;

      if (mode === 'B') {
        for (const tld of tlds) {
          const domain = normalized.includes('.')
            ? normalized
            : `${normalized}.${tld}`;
          all.push(domain);
        }
      } else {
        all.push(normalized);
      }
    }
  }

  // Deduplicate
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const d of all) {
    if (!seen.has(d)) {
      seen.add(d);
      unique.push(d);
    }
  }

  // Filter to valid domains and cap at limit
  return unique
    .filter((d) => validateDomain(d).valid)
    .slice(0, MAX_DOMAINS_PER_REQUEST);
}

export default function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { check, cancel } = useDomainCheck();
  const shortlist = useShortlist();

  const domainsToCheck = useMemo(
    () => buildDomainList(state.inputText, state.mode, state.tlds),
    [state.inputText, state.mode, state.tlds],
  );

  const handleSubmit = useCallback(async () => {
    if (domainsToCheck.length === 0) return;

    dispatch({ type: 'START_CHECK' });

    try {
      const response = await check(
        domainsToCheck,
        state.mode === 'B' ? state.tlds : undefined,
      );
      dispatch({
        type: 'CHECK_SUCCESS',
        payload: {
          results: response.results,
          truncated: response.truncated,
          truncatedCount: response.truncatedCount,
        },
      });
    } catch (err: unknown) {
      // AbortError means user cancelled -- go back to input quietly
      if (err instanceof DOMException && err.name === 'AbortError') {
        dispatch({ type: 'CHECK_ERROR', payload: '' });
        // Clear the empty error so input screen is clean
        dispatch({ type: 'SET_INPUT_TEXT', payload: state.inputText });
        return;
      }
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred.';
      dispatch({ type: 'CHECK_ERROR', payload: message });
    }
  }, [domainsToCheck, check, state.mode, state.tlds, state.inputText]);

  const handleCancel = useCallback(() => {
    cancel();
    dispatch({ type: 'CHECK_ERROR', payload: '' });
    // Clear the empty error
    dispatch({ type: 'SET_INPUT_TEXT', payload: state.inputText });
  }, [cancel, state.inputText]);

  const handleBackToInput = useCallback(() => {
    // Transition back to input phase, preserving the inputText
    dispatch({ type: 'CHECK_ERROR', payload: '' });
    dispatch({ type: 'SET_INPUT_TEXT', payload: state.inputText });
  }, [state.inputText]);

  const handleRetryDomain = useCallback(async () => {
    const domain = state.selectedDomain;
    if (!domain) return;

    dispatch({ type: 'SELECT_DOMAIN', payload: null });
    dispatch({ type: 'START_CHECK' });

    try {
      const response = await check([domain]);
      // Merge the retried result back into existing results
      const existingResults = state.results.filter((r) => r.domain !== domain);
      dispatch({
        type: 'CHECK_SUCCESS',
        payload: {
          results: [...existingResults, ...response.results],
          truncated: state.truncated,
          truncatedCount: state.truncatedCount,
        },
      });
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        dispatch({ type: 'CHECK_ERROR', payload: '' });
        dispatch({ type: 'SET_INPUT_TEXT', payload: state.inputText });
        return;
      }
      const message =
        err instanceof Error ? err.message : 'Failed to retry domain check.';
      dispatch({ type: 'CHECK_ERROR', payload: message });
    }
  }, [state.selectedDomain, state.results, state.truncated, state.truncatedCount, state.inputText, check]);

  const selectedResult = useMemo(
    () =>
      state.selectedDomain
        ? state.results.find((r) => r.domain === state.selectedDomain) ?? null
        : null,
    [state.selectedDomain, state.results],
  );

  return (
    <Layout
      onToggleShortlist={() => dispatch({ type: 'TOGGLE_SHORTLIST_PANEL' })}
      shortlistCount={shortlist.count}
      onShowPrivacy={() => dispatch({ type: 'TOGGLE_PRIVACY' })}
    >
      {/* Main content based on phase */}
      {state.phase === 'input' && (
        <InputScreen
          inputText={state.inputText}
          tlds={state.tlds}
          mode={state.mode}
          onInputChange={(text) =>
            dispatch({ type: 'SET_INPUT_TEXT', payload: text })
          }
          onTldsChange={(tlds) =>
            dispatch({ type: 'SET_TLDS', payload: tlds })
          }
          onModeChange={(mode) =>
            dispatch({ type: 'SET_MODE', payload: mode })
          }
          onSubmit={handleSubmit}
          error={state.error}
        />
      )}

      {state.phase === 'checking' && (
        <CheckProgress
          domains={domainsToCheck}
          onCancel={handleCancel}
        />
      )}

      {state.phase === 'results' && (
        <ResultsTable
          results={state.results}
          onSelectDomain={(domain) =>
            dispatch({ type: 'SELECT_DOMAIN', payload: domain })
          }
          shortlist={shortlist.shortlist}
          onToggleShortlist={shortlist.toggle}
          onBackToInput={handleBackToInput}
          truncated={state.truncated}
          truncatedCount={state.truncatedCount}
        />
      )}

      {/* Drill-in panel */}
      {selectedResult && (
        <DrillInPanel
          result={selectedResult}
          onClose={() =>
            dispatch({ type: 'SELECT_DOMAIN', payload: null })
          }
          isShortlisted={shortlist.has(selectedResult.domain)}
          onToggleShortlist={() => shortlist.toggle(selectedResult.domain)}
          onRetry={handleRetryDomain}
        />
      )}

      {/* Shortlist panel */}
      {state.showShortlist && (
        <ShortlistView
          results={state.results}
          shortlist={shortlist.shortlist}
          onRemove={shortlist.remove}
          onClear={shortlist.clear}
          onClose={() => dispatch({ type: 'TOGGLE_SHORTLIST_PANEL' })}
          onSelectDomain={(domain) => {
            dispatch({ type: 'TOGGLE_SHORTLIST_PANEL' });
            dispatch({ type: 'SELECT_DOMAIN', payload: domain });
          }}
        />
      )}

      {/* Privacy modal */}
      {state.showPrivacy && (
        <PrivacyPage
          onClose={() => dispatch({ type: 'TOGGLE_PRIVACY' })}
        />
      )}
    </Layout>
  );
}
